/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {
  DISMISS_MSG,
  INDUSTRY_COLDEF_NO_FILTER,
  PROPULSION_COLDEF,
  VEHICLE_COLDEF_NO_FILTER
} from '../../../common/constants';
import {IndustryScenario} from '../../../models/industry-scenario-dto.model';
import {PropulsionScenario} from '../../../models/propulsion-scenario-dto-model';
import {VehicleScenario} from '../../../models/vehicle-scenario-dto.model';
import {GlobalScenarioService} from '../../../services/global-scenario-service';
import {AutocompleteFilterComponent} from '../../filters/autocomplete-filter/autocomplete-filter.component';

@Component({
    selector: 'app-global-scenario-select-component',
    templateUrl: './global-scenario-select.component.html',
    styleUrl: './global-scenario-select.component.scss',
    standalone: false
})
export class GlobalScenarioSelectComponent implements OnInit {
  // Data
  @Input() isVehicle: boolean;
  @Input() memberScenarioType: number;
  @Input() filterNonGM: boolean;
  @Input() user: string;
  @Input() displayedMemberColumns: ColDef[];
  @Output() confirmEvent = new EventEmitter<any>();

  showSpinner: boolean;

  globalScenarios: any[] = [];
  memberScenarios: any[] = [];
  vehicleScenarios: VehicleScenario[] = [];
  industryScenarios: IndustryScenario[] = [];
  propulsionScenarios: PropulsionScenario[] = [];
  selectedGlobalScenario: any;
  selectedMemberScenarios: any[] = [];

  globalColumnDef: ColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      lockPosition: true,
      filter: AutocompleteFilterComponent
    },
    {
      field: 'globalScenarioName',
      headerName: 'Global Scenario Name',
      lockPosition: true,
      filter: AutocompleteFilterComponent
    },
  ];

  rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
  };
  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };

  constructor(public snackBar: FabMessageService,
              readonly globalScenarioService: GlobalScenarioService) {
  }

  ngOnInit(): void {
    this.memberScenarios = [];

    // If there are no headers passed in, use the default headers
    if (!this.displayedMemberColumns) {
      switch (this.memberScenarioType) {
        case 0:
          this.displayedMemberColumns = VEHICLE_COLDEF_NO_FILTER;
          break;
        case 1:
          this.displayedMemberColumns = INDUSTRY_COLDEF_NO_FILTER;
          break;
        case 2:
          this.displayedMemberColumns = PROPULSION_COLDEF;
          break;
        default:
          this.displayedMemberColumns = VEHICLE_COLDEF_NO_FILTER;
      }
    }
    this.getGlobalScenarios();
  }

  /**
   * Get list of Global Scenarios to populate table for user selection
   */
  getGlobalScenarios(): void {
    this.showSpinner = true;
    this.globalScenarioService.getAllGlobalScenarios().subscribe(data => {
      this.globalScenarios = data;
    }, err => {
      this.showSpinner = false;
      this.snackBar.displayError(err, DISMISS_MSG);
    }, () => {
      this.showSpinner = false;
    });
  }

  getMemberScenarios(globalScenarioId: number): void {
    this.showSpinner = true;
    this.globalScenarioService.getMemberVehicleScenariosByGmid(globalScenarioId, this.user).subscribe({
      next: (data) => {
        this.vehicleScenarios = data;
      },
      error: (error) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      complete: () => {
        if (this.filterNonGM) {
          this.vehicleScenarios = this.vehicleScenarios.filter(row => !row.fcorg.includes('NONGM'));
        }
        this.industryScenarios = this.vehicleScenarios.filter(row => !row.fcorg.includes('NONGM')).map(element => element.industryScenario);
        this.propulsionScenarios = this.vehicleScenarios.map(element => element.propulsionScenario);

        switch (this.memberScenarioType) {
          case 0:
            this.memberScenarios = this.vehicleScenarios;
            break;
          case 1:
            this.memberScenarios = this.industryScenarios;
            break;
          case 2:
            this.memberScenarios = this.propulsionScenarios;
            break;
        }
        this.selectedMemberScenarios = this.memberScenarios;
        this.showSpinner = false;
        this.onSelect();
      }
    });
  }

  /**
   * Calls the proper function to fetch the member scenarios based on the selected scenario
   * @param event the event when a scenario is selected
   */
  onSelectGlobal(event): void {
    if (event.node.selected) {
      this.selectedGlobalScenario = event.data;
      this.getMemberScenarios(event.data.id);
    }
  }


  /**
   * Return selected row.
   */
  onSelect(): void {
    this.confirmEvent.emit({
      globalScenario: this.selectedGlobalScenario,
      memberScenarios: this.selectedMemberScenarios
    });
  }
}
